// Списъкът в дясната колона — чиста логика без React (тестове:
// reservationList.test.ts): табовете, подредбата и търсенето.

import type { Reservation } from "../../../api/reservations";
import { reservationStartMs, tableNameKey } from "./tableStatus";

/** Предстоящи / Настанени / Минали (приключили, отказани и неизпълнени). */
export type ListTab = "upcoming" | "seated" | "past";

export const LIST_TABS: ListTab[] = ["upcoming", "seated", "past"];

export const listTabOf = (reservation: Reservation): ListTab => {
  if (reservation.status === "upcoming") return "upcoming";
  if (reservation.status === "seated") return "seated";
  return "past";
};

const byStartAsc = (left: Reservation, right: Reservation) =>
  reservationStartMs(left) - reservationStartMs(right) || left.id - right.id;

/**
 * Резервациите по табове: предстоящите и настанените — по час нагоре,
 * миналите — по час надолу (последната приключила е най-отгоре).
 */
export const groupByTab = (reservations: Reservation[]): Record<ListTab, Reservation[]> => {
  const groups: Record<ListTab, Reservation[]> = {
    upcoming: [],
    seated: [],
    past: [],
  };
  for (const reservation of reservations) groups[listTabOf(reservation)].push(reservation);
  groups.upcoming.sort(byStartAsc);
  groups.seated.sort(byStartAsc);
  groups.past.sort((left, right) => byStartAsc(right, left));
  return groups;
};

const digitsOf = (text: string) => text.replace(/\D/g, "");

// Българският номер в една форма — „+359 888…", „00359 888…", „+359 0888…" и
// „0888…" стават „0888…" (както ключът на госта в справка „Гости", BE
// `phone_key`): търсене „0888 123" намира „+359 888 123 456" и обратно.
const nationalDigitsOf = (digits: string) => {
  const withoutPrefix = digits.startsWith("00") ? digits.slice(2) : digits;
  if (!withoutPrefix.startsWith("359")) return withoutPrefix;
  const national = withoutPrefix.slice(3);
  return national.startsWith("0") ? national : `0${national}`;
};

// „маса 4" / „table 4" / „м4" → „4" (търсене по маса).
const TABLE_PREFIX = /^(маса|м\.?|table|t\.?)\s*/u;

/**
 * Търсенето „Име, телефон или маса": името съдържа текста (без значение
 * на главни/малки), телефонът съдържа цифрите му (интервалите не пречат),
 * или името на маса съвпада („4", „Маса 4", „тераса 2").
 */
export const matchesSearch = (reservation: Reservation, query: string) =>
  matchesGuest(reservation, query) || reservation.tableNames.some((name) => matchesTableName(name, query));

/**
 * Гостът съвпада с търсенето: името съдържа текста или телефонът — цифрите му
 * (поне две), като написани, или в националната форма (+359 ↔ 0).
 */
export const matchesGuest = (reservation: Reservation, query: string) => {
  const key = query.trim().toLocaleLowerCase();
  if (key === "") return true;
  if (reservation.guestName.toLocaleLowerCase().includes(key)) return true;
  const digits = digitsOf(key);
  if (digits.length < 2) return false;
  const phoneDigits = digitsOf(reservation.phone);
  if (phoneDigits.includes(digits)) return true;
  const nationalQuery = nationalDigitsOf(digits);
  return nationalQuery.length >= 2 && nationalDigitsOf(phoneDigits).includes(nationalQuery);
};

/**
 * Името на маса съвпада с търсенето — цялото, не по част („4" и „Маса 4"
 * намират маса 4, „1" не намира 10–19). Същото правило и в „Таблица на
 * резервациите".
 */
export const matchesTableName = (name: string, query: string) => {
  const key = query.trim().toLocaleLowerCase();
  if (key === "") return true;
  const nameKey = tableNameKey(name);
  return nameKey === key || nameKey === key.replace(TABLE_PREFIX, "");
};
